import { useState, useEffect } from 'react';
import type { ModifierGroupData } from '@/types/api';

interface SelectOption {
  id: number | string;
  name: string;
}

interface MenuItemMenuGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ModifierGroupData) => void;
  initialData?: ModifierGroupData;
  menuGroups: SelectOption[];
  existingMenuGroupIds: number[];
}

export function MenuItemMenuGroupDialog({
  open,
  onClose,
  onSave,
  initialData,
  menuGroups,
  existingMenuGroupIds,
}: MenuItemMenuGroupDialogProps) {
  const [formData, setFormData] = useState<ModifierGroupData>({});
  const isEdit = !!initialData?.uuid;

  useEffect(() => {
    if (open) {
      setFormData(initialData ?? {});
    }
  }, [open, initialData]);

  if (!open) return null;

  const set = (patch: Partial<ModifierGroupData>) => setFormData((prev) => ({ ...prev, ...patch }));

  const availableGroups = menuGroups.filter(
    (g) => Number(g.id) === formData.menuGroupId || !existingMenuGroupIds.includes(Number(g.id))
  );

  const handleSave = () => {
    if (!formData.menuGroupId) return;
    const data: ModifierGroupData = {
      ...formData,
      uuid: formData.uuid || crypto.randomUUID(),
      menuGroupName: menuGroups.find((g) => Number(g.id) === formData.menuGroupId)?.name,
    };
    onSave(data);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {isEdit ? 'Edit' : 'Add'} Modifier Group
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Menu Group *</label>
            <select
              value={formData.menuGroupId ?? ''}
              onChange={(e) => set({ menuGroupId: e.target.value ? parseInt(e.target.value) : undefined })}
              className="input w-full"
              disabled={false}
              required
            >
              <option value="">-- Select Menu Group --</option>
              {availableGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Quantity</label>
              <input
                type="number"
                min="0"
                value={formData.minQuantity ?? ''}
                onChange={(e) => set({ minQuantity: e.target.value ? parseInt(e.target.value) : undefined })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Quantity</label>
              <input
                type="number"
                min="0"
                value={formData.maxQuantity ?? ''}
                onChange={(e) => set({ maxQuantity: e.target.value ? parseInt(e.target.value) : undefined })}
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
            <input
              type="number"
              min="0"
              value={formData.sortOrder ?? ''}
              onChange={(e) => set({ sortOrder: e.target.value ? parseInt(e.target.value) : undefined })}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Ticket Value</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.minTicketValueToBeAvailable ?? ''}
              onChange={(e) => set({ minTicketValueToBeAvailable: e.target.value ? parseFloat(e.target.value) : undefined })}
              className="input w-full"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="mg-selectMax"
                checked={formData.selectMax ?? false}
                onChange={(e) => set({ selectMax: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="mg-selectMax" className="ml-2 text-sm text-gray-700">Select Max</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="mg-selectMaxWithinGroup"
                checked={formData.selectMaxWithinGroup ?? false}
                onChange={(e) => set({ selectMaxWithinGroup: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="mg-selectMaxWithinGroup" className="ml-2 text-sm text-gray-700">Select Max Within Group</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="mg-extra"
                checked={formData.extra ?? false}
                onChange={(e) => set({ extra: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="mg-extra" className="ml-2 text-sm text-gray-700">Extra</label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!formData.menuGroupId}
            className="btn btn-primary"
          >
            {isEdit ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
